import type { MouseEvent } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { useSiteSelectorContext } from "@/components/site-selector-modal";
import Card from "@/components/wpl/card/src";
import { OneButtonProps } from "@/components/wpl/content-block/src";
import { Image } from "@/content/templates/template.types.ts";
import { useLocalizedHref } from "@/libraries/web-framework";

export interface ProductCTAProps {
    name: string | null;
    title: string | null;
    valueProposition: string | null;
    text: string | null;
    url: string | null;
    asset: Image | null;
    productName: string | null;
    dataBlueprintModule: string | null;
}

export const ProductCTA = (props: ProductCTAProps) => {
    const { openModalForTemplate } = useSiteSelectorContext();

    const openSiteSelectorModal = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();

        openModalForTemplate({
            templateId: props.dataBlueprintModule ?? "",
            templateName: props.name ?? "",
            productKey: props.productName as ProductKeys
        });
    };

    const ctaUrl = useLocalizedHref(props.url ?? "");

    const ctaProps = props.dataBlueprintModule
        ? ({
              buttonProps: {
                  text: props.text,
                  inputType: "button",
                  type: "secondary",
                  size: "small",
                  onClick: openSiteSelectorModal // No dataEventProps as the SiteSelectorModal handles the analytics
              }
          } as OneButtonProps)
        : ({
              buttonProps: {
                  text: props.text,
                  type: "secondary",
                  size: "small",
                  url: ctaUrl,
                  dataEventProps: {
                      "data-product-key": "jiraSoftware",
                      "data-event-component": "linkButton",
                      "data-event-container": "siteSelectorModal",
                      "data-event": "clicked"
                  }
              }
          } as OneButtonProps);

    // FIXME: These nested selectors are hacks to force the Card component to look like a product CTA card
    const productCTACss = css({
        "marginBlockStart": "24px",
        "paddingBlockEnd": "32px",
        /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
        ".product-cta-card": {
            borderRadius: "4px",
            boxShadow: "1px 2px 2px 2px rgba(0,0,0,.08)",
            [media.above.sm]: {
                "alignItems": "start !important",
                /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
                ".content-block": {
                    display: "flex !important"
                }
            }
        },
        /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
        ".img-section": {
            padding: "32px",
            backgroundColor: "#f4f5f7",
            display: "flex !important",
            justifyContent: "center",
            alignItems: "center",
            alignSelf: "stretch",
            flexDirection: "column",
            width: "fit-content"
        },
        /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
        ".img-section img": {
            display: "block",
            margin: "auto",
            maxHeight: "200px"
        },
        /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
        "img": {
            width: "auto"
        },
        /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
        ".text-section": {
            /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
            h3: {
                color: "#253858",
                fontSize: "20px",
                fontWeight: "500"
            },
            /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
            p: {
                color: "#253858",
                fontSize: "16px",
                fontWeight: "400"
            }
        },
        /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
        "a": {
            transition: "background-color 0.2s ease-in-out"
        },
        [media.below.md]: {
            /* eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors */
            ".img-section": {
                width: "100%"
            }
        }
    });

    return (
        <div css={productCTACss}>
            <Card
                className="product-cta-card"
                bodyText={props.valueProposition}
                headerText={props.title ?? ""}
                pictureProps={{
                    image: {
                        src: props.asset?.url ?? "",
                        alt: props.asset?.description ?? props.asset?.name ?? ""
                    }
                }}
                orientation="horizontal"
                ctaProps={ctaProps}
            />
        </div>
    );
};
